/* eslint-disable @typescript-eslint/no-explicit-any */
import { Alert, Button, Col, message, Radio, Row, Select } from 'antd-v4';
import { useContext, useEffect, useMemo, useState } from 'react';

import { FirebaseContext } from '../../../context/firebaseContext';
import { useSubscription } from '../../../hooks/useSubscription';
import {
  ProspectType,
  DelayType,
  delayTypeAsString,
  PartnersType,
  ContextStatus,
  isValidPartnersType,
  UserRole
} from '@leedpulse-monorepo/enums';
import { renderProspectTypeOption } from '@leedpulse-monorepo/enums-ui';
import { BackendContext } from '../../../context/backendContext';
import useScenarios from '../conversations/hooks/useScenarios';
import { Checkbox, Empty, Flex, Table } from 'antd';
import { ISmallUser, ISubscription } from '@leedpulse-monorepo/interfaces';
import { ArrowDownOutlined, ArrowUpOutlined, CheckCircleOutlined, ClockCircleOutlined, HourglassOutlined, RobotOutlined, SettingOutlined, StarOutlined, StopOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import {
  IResponse,
  GetIntegrationsOrishaTypologiesResponse,
  ICustomProspectType,
} from '@leedpulse-monorepo/interfaces';
import { updateSubscription } from "../../../services/SubscriptionService";

const GatewayConfig = ({ onSubmit }: { onSubmit: () => void }) => {
  const { subscriptionId, getUsersByConstraints, userData } =
    useContext(FirebaseContext);
  const { getRequest } = useContext(BackendContext);

  const scenarios = useScenarios(subscriptionId || '');

  const { subscription: subscriptionFromHook } = useSubscription() as {
    subscription: ISubscription | null;
  };
  const [subscription, setSubscription] = useState<ISubscription | null>(null);
  const [sales, setSales] = useState<ISmallUser[]>([]);
  const [salesLoading, setSalesLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [isTypologiesToScenariosEnabled, setIsTypologiesToScenariosEnabled] = useState(false);

  const [customProspectTypesStatus, setCustomProspectTypesStatus] = useState(ContextStatus.IDLE);
  const [customProspectTypes, setCustomProspectTypes] = useState<ICustomProspectType[]>([]);

  const isLeedpulseAdmin = useMemo(() => {
    return userData && (userData.role === UserRole.admin || userData.role === UserRole.owner);
  }, [userData]);

  const typologiesDataSource = useMemo(() => {
    const oSubscriptionProspectTypes = Object.entries(
      ProspectType[subscription?.partner_system as PartnersType] || {}
    ).map((entry) => {
      return {
        label: renderProspectTypeOption(entry[1], subscription?.partner_system as PartnersType),
        value: entry[1],
      };
    });

    const oCustomProspectTypes = customProspectTypes.map((typology) => {
      return {
        label: typology.name,
        value: typology.id,
      };
    });

    return [...oSubscriptionProspectTypes, ...oCustomProspectTypes];
  }, [subscription, customProspectTypesStatus]);

  const refreshIsTypologiesToScenariosEnabled = (pSubscription: ISubscription) => {
    setIsTypologiesToScenariosEnabled(
      (isLeedpulseAdmin || false) &&
      !!(
        pSubscription?.partner_settings?.prospects_typologies_to_scenarios &&
        Object.keys(pSubscription?.partner_settings?.prospects_typologies_to_scenarios).length > 0
      )
    );
  };

  const typologiesToScenariosDataSource = useMemo(() => {
    if (!subscription?.partner_settings?.prospects_typologies_to_scenarios) return [];

    const dataSource =
      subscription.partner_settings.prospects_typologies?.map((typology) => ({
        typology,
        scenario: subscription.partner_settings.prospects_typologies_to_scenarios?.[typology],
      })) || [];

    return dataSource;
  }, [subscription]);

  // Ce rafraichissement vivait DANS le useMemo ci-dessus. Appeler un setState
  // depuis un memo peut boucler (react/set-state-in-render) : React peut
  // rejouer le memo, qui re-declenche le setState. Il passe dans un effet,
  // declenche par le meme calcul. Le setState reste synchrone dans l'effet
  // faute de pouvoir deriver ce flag : il est aussi ecrit depuis deux handlers.
  useEffect(() => {
    if (subscription && typologiesToScenariosDataSource.length <= 0) {
      // eslint-disable-next-line react/set-state-in-effect
      refreshIsTypologiesToScenariosEnabled(subscription);
    }
  }, [typologiesToScenariosDataSource, subscription]);

  const fetchPartnerCustomProspectTypes = async (pSubscription: ISubscription) => {
    setCustomProspectTypesStatus(ContextStatus.LOADING);
    try {
      const oProspectTypes: ICustomProspectType[] = [];
      // isValid check but not supposed to happen (page not loaded if invalid partner_system)
      if (isValidPartnersType(pSubscription?.partner_system as PartnersType)) {
        // Filter out typologies that are already in the default prospect types to avoir refetch
        const filteredTypologies = pSubscription?.partner_settings?.prospects_typologies?.filter(
          (typology) =>
            !Object.values(ProspectType[pSubscription?.partner_system as PartnersType]).includes(
              typology
            )
        );

        // If there are something to fetch
        if (filteredTypologies && filteredTypologies.length > 0) {
          if (pSubscription?.partner_system === PartnersType.orisha) {
            const urlParams = new URLSearchParams();
            urlParams.set('ids', filteredTypologies?.join(',') || '');

            // Fetch for orisha
            const response: IResponse<GetIntegrationsOrishaTypologiesResponse> = await getRequest(
              `/api/v1/integrations/orisha/typologies?${urlParams.toString()}`,
              {
                'x-api-key': pSubscription?.api_key,
              }
            );

            oProspectTypes.push(...response.data.groups);
          } else if (pSubscription?.partner_system === PartnersType.septeo) {
            // Not yet implemented
            console.log('septeo');
          }
        }
      }
      setCustomProspectTypes(oProspectTypes);
      setCustomProspectTypesStatus(ContextStatus.SUCCESS);
    } catch (error) {
      setCustomProspectTypesStatus(ContextStatus.ERROR);
    }
  };

  useEffect(() => {
    if (subscriptionFromHook) {
      // Synchro avec une source distante : l'etat vient d'un appel reseau, pas du
      // rendu. C'est l'usage d'un effet que la regle autorise ("synchronizing with an
      // external system") ; le setState synchrone est le reset qui precede l'appel.
      // eslint-disable-next-line react/set-state-in-effect
      setSubscription(subscriptionFromHook);
      // Check if typologies to scenario is not empty
      refreshIsTypologiesToScenariosEnabled(subscriptionFromHook);

      // Fetch partner custom prospect types to orisha api
      fetchPartnerCustomProspectTypes(subscriptionFromHook);

      const loadSales = async () => {
        setSalesLoading(true);
        const lpUsers = await getUsersByConstraints([
          {
            field: `id_subscription.${subscriptionFromHook.id}`,
            op: 'orderBy',
            value: 'asc',
          },
        ]);

        const aSales = lpUsers.doc?.map((lpUser: any) => ({ ...lpUser } as ISmallUser)) || [];

        setSales(aSales);
        setSalesLoading(false);
      };

      loadSales();
    }
  }, [subscriptionFromHook]);

  const filterTypologiesToScenariosOrphan = (pSubscription: ISubscription) => {
    const typologies = pSubscription.partner_settings.prospects_typologies || [];
    const typologiesToScenarios = typologies.map((typology) => ({
      [typology]: pSubscription.partner_settings.prospects_typologies_to_scenarios?.[typology],
    }));

    return Object.assign({}, ...typologiesToScenarios);
  };

  const handleSubmit = async () => {
    if (!subscription) return;
    setLoading(true);
    if (
      subscription?.partner_settings?.import_mode === 'auto' ||
      subscription?.partner_settings?.import_mode === 'manual'
    ) {
      // `handleSubmit` construit le payload en mutant l'objet `subscription` du
      // hook, puis l'envoie. Le passer en immuable veut dire refaire tout le
      // flux de donnees de cette soumission (5 blocs, 2 chemins d'import) sur un
      // ecran sans test : cf. TECH_DEBT.md, « handleSubmit de gateway-config.tsx
      // construit son payload en mutant l'objet du hook ».
      // eslint-disable-next-line react/immutability
      subscription.partner_settings = {
        import_mode: subscription?.partner_settings?.import_mode,
        prospects_typologies: [],
        users_uuids: [],
        prospects_seniority: '90_days',
        prospects_seniority_max: '1095_days',
        include_archived: false,
        max_credit_consumption_per_month: 100,
      } as ISubscription['partner_settings'];
    }

    if (isTypologiesToScenariosEnabled || subscription?.partner_settings?.prospects_typologies_to_scenarios) {
      // Build prospects_typologies_to_scenarios object (remove orphan scenarios)
      // Meme mutation du payload, meme raison. Voir TECH_DEBT.
      // eslint-disable-next-line react/immutability
      subscription.partner_settings.prospects_typologies_to_scenarios =
        filterTypologiesToScenariosOrphan(subscription);

      // Clean prospects_typologies_to_scenarios to remove all undefined values in the map
      subscription.partner_settings.prospects_typologies_to_scenarios = subscription
        .partner_settings.prospects_typologies_to_scenarios
        ? Object.entries(subscription.partner_settings.prospects_typologies_to_scenarios)
          .filter(([k, v]) => v !== 'default' && v !== undefined)
          .reduce((acc, [k, v]) => {
            acc[k] = v;
            return acc;
          }, {} as { [key: string]: string })
        : {};

      refreshIsTypologiesToScenariosEnabled(subscription);
    }

    const oResUpdateSubs = await updateSubscription(subscription);
    setLoading(false);

    if (oResUpdateSubs.success) {
      message.success('La passerelle a été configurée avec succès.');
    } else {
      message.error('Une erreur est survenue lors de la configuration de la passerelle');
    }

    onSubmit?.();
  };

  const handleTypologiesToScenariosPositionChange = (typology: string, movement: 'up' | 'down') => {
    const currentTypologies = subscription?.partner_settings?.prospects_typologies || [];

    const currentIndex = currentTypologies.findIndex((value) => value === typology);

    if (currentIndex === -1) return;

    // Calculate new index based on movement
    let newIndex: number;
    if (movement === 'up' && currentIndex > 0) {
      newIndex = currentIndex - 1;
    } else if (movement === 'down' && currentIndex < currentTypologies.length - 1) {
      newIndex = currentIndex + 1;
    } else {
      return;
    }

    const newTypologies = [...currentTypologies];
    const [movedEntry] = newTypologies.splice(currentIndex, 1);
    newTypologies.splice(newIndex, 0, movedEntry);

    // Update the subscription with new order
    if (subscription) {
      setSubscription({
        ...subscription,
        partner_settings: {
          ...subscription.partner_settings,
          prospects_typologies: newTypologies,
        },
      });
    }
  };

  return (
    <Row>
      <Col span={24}>
        <div style={{ margin: '1rem 0' }}>
          <h4>Choisissez un mode de configuration</h4>
          <Radio.Group
            size="middle"
            optionType="button"
            value={subscription?.partner_settings?.import_mode}
            options={[
              {
                label: <Flex align="center" gap={6}><RobotOutlined />Mode automatique</Flex>,
                value: 'auto',
              },
              {
                label: <Flex align="center" gap={6}><SettingOutlined />Mode personnalisé</Flex>,
                value: 'custom',
              },
              {
                label: <Flex align="center" gap={6}><StopOutlined />Mode manuel</Flex>,
                value: 'manual',
              },
            ]}
            onChange={(e) => {
              if (!subscription) return;
              setSubscription({
                ...subscription,
                partner_settings: {
                  ...subscription.partner_settings,
                  import_mode: e.target.value,
                },
              });
            }}
          />
          <Alert
            style={{ margin: '1rem 0' }}
            showIcon
            closable={false}
            description={
              <>
                {subscription?.partner_settings?.import_mode === 'auto' && (
                  <>
                    <h5>Mode automatique</h5>
                    <i>
                      L'IA utilisera des paramètres optimisés basés sur les meilleures pratiques du
                      secteur immobilier.
                    </i>
                    <p style={{ marginTop: '1rem' }}>
                      <b><StarOutlined /> Types de prospects :</b> Vendeurs et acquéreurs
                      <br />
                      <b><UserOutlined /> Tous les utilisateurs :</b> Tous les utilisateurs de votre logiciel
                      métier
                      <br />
                      <b><HourglassOutlined /> Temps minimum sans action :</b> 3 mois
                      <br />
                    </p>
                  </>
                )}
                {subscription?.partner_settings?.import_mode === 'custom' && (
                  <>
                    <h5>Mode personnalisé</h5>
                    <i>Vous personnalisez tout :</i>
                    <p style={{ marginTop: '1rem' }}>
                      <StarOutlined /> les <b>types de contacts</b> à relancer
                      <br />
                      <UserOutlined /> les <b>utilisateurs</b> concernés
                      <br /><HourglassOutlined /> le <b>temps d’inactivité</b> avant relance automatique
                      <br />
                    </p>
                  </>
                )}
                {subscription?.partner_settings?.import_mode === 'manual' && (
                  <>
                    <h5>Mode désactivé</h5>
                    <i>
                      L'IA n'importera pas vos contacts pour l’instant. Attendez l’appel avec votre
                      chargé de projet pour choisir les bons réglages.
                    </i>
                  </>
                )}
              </>
            }
          />
        </div>
      </Col>

      {subscription?.partner_settings?.import_mode === 'custom' && (
        <>
          <Col span={24}>
            <div style={{ margin: '1rem 0' }}>
              <h4>1. <TeamOutlined /> Qui voulez-vous relancer ?</h4>
              <p className="hp-p1-body">
                Choisissez les profils de prospects à relancer automatiquement :
              </p>
              <Select
                mode="multiple"
                placeholder={<Flex align="center" gap={6}><CheckCircleOutlined /> Tous les prospects</Flex>}
                style={{ width: 450 }}
                maxTagCount={2}
                value={subscription?.partner_settings?.prospects_typologies}
                options={typologiesDataSource}
                onChange={(value) => {
                  setSubscription((o) => {
                    if (!o) return null;
                    const res = {
                      ...o,
                      partner_settings: {
                        ...o.partner_settings,
                        prospects_typologies: value,
                      },
                    };

                    res.partner_settings.prospects_typologies_to_scenarios =
                      filterTypologiesToScenariosOrphan(res);
                    if (
                      isTypologiesToScenariosEnabled &&
                      res.partner_settings.prospects_typologies_to_scenarios === undefined
                    )
                      setIsTypologiesToScenariosEnabled(false);

                    return res;
                  });
                }}
                optionFilterProp="label"
                loading={customProspectTypesStatus === ContextStatus.LOADING}
              />

              {!isTypologiesToScenariosEnabled &&
                subscription?.partner_settings?.prospects_typologies?.length &&
                isLeedpulseAdmin ? (
                <Button type="link" onClick={() => setIsTypologiesToScenariosEnabled(true)}>
                  Ajouter des scénarios customs
                </Button>
              ) : null}

              {isTypologiesToScenariosEnabled ? (
                <div style={{ marginTop: '1rem' }}>
                  {/* <h4>3. <TeamOutlined /> Scénarios customs</h4> */}
                  <p className="hp-p1-body">
                    Choisissez un scénario pour chaque type de prospect :
                  </p>
                  <Flex vertical style={{ width: 450 }}>
                    <Table
                      showHeader={false}
                      pagination={false}
                      style={{ width: '100%' }}
                      locale={{
                        emptyText: <Empty description="Aucune typologie sélectionnée"></Empty>,
                      }}
                      dataSource={typologiesToScenariosDataSource}
                      columns={[
                        {
                          title: 'Typologie',
                          dataIndex: 'typology',
                          key: 'typology',
                          render: (typology) => {
                            return (
                              customProspectTypes.find(
                                (customProspectType) => customProspectType.id === typology
                              )?.name ??
                              prospectTypeAsString(
                                typology,
                                subscription?.partner_system as PartnersType
                              )
                            );
                          },
                        },
                        {
                          title: 'Scénario',
                          dataIndex: 'scenario',
                          key: 'scenario',
                          render: (scenario, record) => {
                            return (
                              <Select // Select wich scenario use for this typology
                                style={{ width: '100%', maxWidth: '225px' }}
                                options={[{ name: 'Par défaut', id: 'default' }, ...scenarios].map(
                                  (scenario) => {
                                    return {
                                      label: scenario.name,
                                      value: scenario.id,
                                    };
                                  }
                                )}
                                onChange={(scenarioId) => {
                                  setSubscription({
                                    ...subscription,
                                    partner_settings: {
                                      ...subscription.partner_settings,
                                      prospects_typologies_to_scenarios: {
                                        ...subscription.partner_settings
                                          .prospects_typologies_to_scenarios,
                                        [record.typology]: scenarioId,
                                      },
                                    },
                                  });
                                }}
                                value={
                                  subscription?.partner_settings
                                    ?.prospects_typologies_to_scenarios?.[record.typology] ??
                                  'Automatique'
                                }
                                // @ts-expect-error antd-v4 type
                                optionRender={(option) => {
                                  return (
                                    <Flex vertical gap={4}>
                                      <span>{option.label}</span>
                                      <span
                                        style={{
                                          fontStyle: 'italic',
                                          fontWeight: 'normal',
                                        }}
                                      >
                                        {option.value}
                                      </span>
                                    </Flex>
                                  );
                                }}
                                allowClear
                              />
                            );
                          },
                        },
                        {
                          title: 'Actions',
                          dataIndex: 'actions',
                          key: 'actions',
                          render: (_, record, index) => {
                            return (
                              <Flex vertical gap={4} align="center" justify="center">
                                {index > 0 ? (
                                  <Button
                                    size="small"
                                    shape="circle"
                                    icon={<ArrowUpOutlined />}
                                    onClick={() =>
                                      handleTypologiesToScenariosPositionChange(
                                        record.typology,
                                        'up'
                                      )
                                    }
                                  />
                                ) : null}

                                {index < typologiesToScenariosDataSource?.length - 1 ? (
                                  <Button
                                    size="small"
                                    shape="circle"
                                    icon={<ArrowDownOutlined />}
                                    onClick={() =>
                                      handleTypologiesToScenariosPositionChange(
                                        record.typology,
                                        'down'
                                      )
                                    }
                                  />
                                ) : null}
                              </Flex>
                            );
                          },
                        },
                      ]}
                    />
                  </Flex>
                </div>
              ) : null}
            </div>
          </Col>

          <Col span={24}>
            <div style={{ margin: '1rem 0' }}>
              <h4>2. <UserOutlined /> Pour quels utilisateurs ?</h4>
              <p className="hp-p1-body">
                Sélectionnez les utilisateurs qui recevront l’aide de l’IA pour relancer leurs
                contacts :
              </p>
              <Select
                mode="multiple"
                placeholder={<Flex align="center" gap={6}><CheckCircleOutlined /> Tous les commerciaux</Flex>}
                style={{ width: 450 }}
                value={(subscription?.partner_settings?.users_uuids ?? []).filter((uuid) =>
                  sales.some((user) => user.partner_uid === uuid)
                )}
                loading={salesLoading}
                maxTagCount={2}
                options={[
                  {
                    label: <Flex align="center" gap={6}><TeamOutlined /> Tous les commerciaux</Flex>,
                    value: 'all',
                  },
                  ...sales
                    .sort((a, b) => {
                      return a.firstname.toLowerCase().localeCompare(b.firstname.toLowerCase());
                    })
                    .filter((oSale) => !!oSale.partner_uid)
                    .map((oSale) => {
                      return {
                        label: <Flex align="center" gap={6}><UserOutlined /> {oSale.firstname} {oSale.lastname}</Flex>,
                        value: oSale.partner_uid,
                      };
                    }),
                ]}
                onChange={(value) => {
                  if (value.includes('all')) {
                    setSubscription({
                      ...subscription,
                      partner_settings: {
                        ...subscription.partner_settings,
                        users_uuids: [],
                      },
                    });
                  } else {
                    setSubscription({
                      ...subscription,
                      partner_settings: {
                        ...subscription.partner_settings,
                        users_uuids: value,
                      },
                    });
                  }
                }}
              />
            </div>
          </Col>

          <Col span={24}>
            <div style={{ margin: '1rem 0' }}>
              <h4>3. <ClockCircleOutlined /> Quelle plage de temps sans action ?</h4>
              <p className="hp-p1-body">
                Définissez la période d'inactivité pour déclencher automatiquement les relances :
              </p>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Temps d'inactivité minimum
                  </label>
                  <Select
                    style={{ width: 250 }}
                    placeholder="Temps d'inactivité minimum..."
                    value={subscription?.partner_settings?.prospects_seniority}
                    options={Object.entries(DelayType).map((entry) => {
                      return {
                        label: delayTypeAsString(entry[1]),
                        value: entry[1],
                      };
                    })}
                    onChange={(value) => {
                      setSubscription({
                        ...subscription,
                        partner_settings: {
                          ...subscription.partner_settings,
                          prospects_seniority: value,
                        },
                      });
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Temps d'inactivité maximum
                  </label>
                  <Select
                    style={{ width: 250 }}
                    placeholder="Temps d'inactivité maximum..."
                    value={subscription?.partner_settings?.prospects_seniority_max ?? "1095_days"}
                    options={[{ label: "1 an", value: "365_days" }, { label: "2 ans", value: "730_days" }, { label: "3 ans", value: "1095_days" }, { label: "5 ans", value: "1825_days" }, { label: "7 ans", value: "2555_days" }, { label: "Sans limite", value: "unlimited" }]}
                    onChange={(value) => {
                      setSubscription({
                        ...subscription,
                        partner_settings: {
                          ...subscription.partner_settings,
                          prospects_seniority_max: value,
                        },
                      });
                    }}
                  />
                </div>
              </div>
            </div>
          </Col>
          <Col span={24}>
            <div style={{ margin: '1rem 0' }}>
              <h4>4. <SettingOutlined /> Options avancées</h4>
              <p className="hp-p1-body">
                Configurez les options avancées pour personnaliser encore plus votre passerelle :
              </p>

              {subscription?.partner_system === PartnersType.septeo && (
                <Checkbox
                  checked={subscription?.partner_settings?.include_archived ?? false}
                  onChange={(e) => {
                    if (!subscription) return;
                    setSubscription({
                      ...subscription,
                      partner_settings: {
                        ...subscription.partner_settings,
                        include_archived: e.target.checked,
                      },
                    });
                  }}
                  style={{ display: "flex", alignItems: "center", marginLeft:'0.5rem' }}
                >
                  <div>
                    <div style={{ paddingTop: "3px" }}>Inclure les contacts archivés</div>
                  </div>
                </Checkbox>
              )}

               {[PartnersType.septeo, PartnersType.orisha].includes(subscription?.partner_system as PartnersType) && (
                <Checkbox
                  checked={!(subscription?.partner_settings?.include_buyers_without_identified_search ?? true)}
                  onChange={(e) => {
                    if (!subscription) return;
                    setSubscription({
                      ...subscription,
                      partner_settings: {
                        ...subscription.partner_settings,
                        include_buyers_without_identified_search: !e.target.checked, // UI displays "exclude" but stored value is "include"
                      },
                    });
                  }}
                  style={{ display: "flex", alignItems: "center", marginTop: "0.5rem" }}
                >
                  <div>
                    <div style={{ paddingTop: "3px" }}>Exclure les acheteurs sans fiche acquéreur</div>
                  </div>
                </Checkbox>)}

               {[PartnersType.septeo, PartnersType.orisha].includes(subscription?.partner_system as PartnersType) && (
                <Checkbox
                  checked={!(subscription?.partner_settings?.include_sellers_without_estimation ?? true)}
                  onChange={(e) => {
                    if (!subscription) return;
                    setSubscription({
                      ...subscription,
                      partner_settings: {
                        ...subscription.partner_settings,
                        include_sellers_without_estimation: !e.target.checked, // UI displays "exclude" but stored value is "include"
                      },
                    });
                  }}
                  style={{ display: "flex", alignItems: "center", marginTop: "0.5rem" }}
                >
                  <div>
                    <div style={{ paddingTop: "3px" }}>Exclure les vendeurs sans estimation</div>
                  </div>
                </Checkbox>)}

               {[PartnersType.septeo, PartnersType.orisha].includes(subscription?.partner_system as PartnersType) && (
                <Checkbox
                  checked={subscription?.partner_settings?.include_contacts_without_typology ?? false}
                  onChange={(e) => {
                    if (!subscription) return;
                    setSubscription({
                      ...subscription,
                      partner_settings: {
                        ...subscription.partner_settings,
                        include_contacts_without_typology: e.target.checked,
                      },
                    });
                  }}
                  style={{ display: "flex", alignItems: "center", marginTop: "0.5rem" }}
                >
                  <div>
                    <div style={{ paddingTop: "3px" }}>Inclure les contacts sans typologie</div>
                  </div>
                </Checkbox>)}

               {[PartnersType.septeo, PartnersType.orisha].includes(subscription?.partner_system as PartnersType) && (
                <Checkbox
                  checked={subscription?.partner_settings?.archive_prospects_without_interest ?? false}
                  onChange={(e) => {
                    if (!subscription) return;
                    setSubscription({
                      ...subscription,
                      partner_settings: {
                        ...subscription.partner_settings,
                        archive_prospects_without_interest: e.target.checked,
                      },
                    });
                  }}
                  style={{ display: "flex", alignItems: "center", marginTop: "0.5rem" }}
                >
                  <div>
                    <div style={{ paddingTop: "3px" }}>Archiver automatiquement dans mon CRM les prospects qui ne sont plus en recherche</div>
                  </div>
                </Checkbox>)}

              <div style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Nombre max de contacts à importer par mois
                </label>
                <Select
                  style={{ width: 250 }}
                  value={subscription?.partner_settings?.max_credit_consumption_per_month ?? 100}
                  options={[
                    { label: '50', value: 50 },
                    { label: '100', value: 100 },
                    { label: '150', value: 150 },
                    { label: '200', value: 200 },
                    { label: '300', value: 300 },
                    { label: '400', value: 400 },
                    { label: '500', value: 500 },
                    { label: '1000', value: 1000 },
                  ]}
                  onChange={(value) => {
                    setSubscription({
                      ...subscription,
                      partner_settings: {
                        ...subscription.partner_settings,
                        max_credit_consumption_per_month: value,
                      },
                    });
                  }}
                />
              </div>
            </div>
          </Col>
        </>
      )}

      <Col span={24} className="hp-profile-action-btn hp-mb-16">
        <Button
          style={{ marginTop: '1rem' }}
          type="primary"
          disabled={loading}
          onClick={() => handleSubmit()}
        >
          Enregistrer
        </Button>
      </Col>
    </Row>
  );
};

export default GatewayConfig;
